/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useEffect, useState } from "react";
import { API } from "../../../globals/globals";
import { useSelector } from "react-redux";
import { useExportNodeDevicesMutation, useGetAlarmsByDeviceKeyMutation, useGetNodesQuery } from "../../../store/api/devices/deviceApiSlice";
import { urlParameterSelector } from "../../../store/filters/Nodes/selectors";
import { nodesColumns } from "../../../components/Shared/Table/Columns";
import { searchKeywordSelector } from "../../../store/search/selector";
import { useStoreDispatch } from "../../../store/hooks";
import { setIsLoadingStatus } from "../../../store/helpers/helperReducers";
import { paginationModelSelector } from "../../../store/helpers/helperSelectors";
import { useGetDrawerCountsMutation } from "../../../store/api/devices/deviceApiSlice";
import {
  setSelectedDevice,
  setSelectedDeviceAlarms,
  setSelectedDeviceAlarmCount,
  setOpenDeviceDrawer,
  setDrawerCounts,
} from "../../../store/api/devices/reducers";

import { setOpenToast } from "../../../store/helpers/helperReducers";
import { useGetDeviceFullDetailMutation } from "../../../store/api/deviceConfig/deviceConfigApiSlice";
import { GridEventListener, GridRowParams } from "@mui/x-data-grid";
import { IServiceDeviceData } from "../../../store/api/devices/types";
import { errorMessage, exportInCSV } from "../../../utils/helpers";
import { selectProfilePermissionsSelector } from "../../../store/api/auth/selectors";
import ProfilePermissions from "../../../utils/ProfilePermissions";
import { initialState } from "../../../store/helpers/helperInitialState";

export const useNodeDisplay = (skipQuery: boolean = false) => {
  const dispatch = useStoreDispatch();
  const [selectedRowData, setSelectedRowData] = useState<IServiceDeviceData>();
  const [rowClicked, setRowClicked] = useState(false);
  const [sortOrder, setSortOrderToNode] = useState("name, desc");

  const filters = useSelector(urlParameterSelector);
  const keyword = useSelector(searchKeywordSelector);
  const { nodeDisplay = initialState.pagination } = useSelector(paginationModelSelector);
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);

  const [getDrawerCounts] = useGetDrawerCountsMutation();
  const [getDeviceFullDetail] = useGetDeviceFullDetailMutation();
  const [getAlarmsByDeviceKey] = useGetAlarmsByDeviceKeyMutation();
  const [exportDevices, exportStatus] = useExportNodeDevicesMutation();

  filters.set("sort", sortOrder);
  filters.set("page", nodeDisplay.page.toString());
  filters.set("size", nodeDisplay.pageSize.toString());
  filters.set("search", keyword);

  const args = { filters: filters.toString() };
  const { data, isLoading, refetch } = useGetNodesQuery(args, {
    pollingInterval: API.ENDPOINTS.NODES.pollingInterval || API.POLLING_INTERVAL,
    skip: skipQuery,
  });

  // This will force the data to update each time the user clicks the "Devices" tab
  //  and any time the filter selections are changed
  useEffect(() => {
    if (!skipQuery) {
      refetch();
    }
  }, [filters, refetch, keyword, skipQuery]);

  const handleRowClicked: GridEventListener<"rowClick"> = async (params: GridRowParams<IServiceDeviceData>, event) => {
    if (params.row.modelName === "migration" || params.row.modelName === "unknown") {
      // This is a migrated device that has not yet connected to SNET
      return;
    }

    dispatch(setIsLoadingStatus({ isLoadingDeviceDetail: true, isLoadingAlarms: true, isLoadingCounts: true }));
    setRowClicked(true);
    dispatch(setOpenDeviceDrawer(true));
    setSelectedRowData(params.row);

    try {
      const args = { filters: `key=objectKey&value=${params.row.objectKey}&weightType=FULL`, deviceKey: params.row.objectKey };

      const deviceDataResponse = await getDeviceFullDetail(args);
      if ("error" in deviceDataResponse) {
        const { error = "" } = { ...deviceDataResponse };
        throw !!error && error;
      } else if ("data" in deviceDataResponse) {
        const { data = "" } = { ...deviceDataResponse };

        if (!!data && data?.data[0]) {
          dispatch(setIsLoadingStatus({ isLoadingDeviceDetail: false }));
          dispatch(setSelectedDevice({ ...data.data[0], ...data?.deviceParams }));
        }
      }

      const configCountResponse = await getDrawerCounts({
        deviceKey: params.row.objectKey,
        filters: "",
      })

      if ("error" in configCountResponse) {
        const { error = "" } = { ...configCountResponse };
        throw !!error && error;
      } else if ("data" in configCountResponse) {
        const { data = "" } = { ...configCountResponse };

        if (!!data) {
          dispatch(setDrawerCounts(data));
          dispatch(setIsLoadingStatus({ isLoadingCounts: false }));
        }
      }

      let alarmDataResponse;
      if (permissions?.hasOpCodeGroup("view_events")) {
        alarmDataResponse = await getAlarmsByDeviceKey({
          deviceKey: params.row.objectKey,
          filters: "?sort=retention%2Cdesc&page=0&size=50",
        });

        if ("error" in alarmDataResponse) {
          const { error = "" } = { ...alarmDataResponse };
          throw !!error && error;
        } else if (alarmDataResponse && "data" in alarmDataResponse) {
          const { data = "" } = { ...alarmDataResponse };

          if (permissions?.hasOpCodeGroup("view_events") && data) {
            dispatch(setSelectedDeviceAlarms(data?.data));
            dispatch(setSelectedDeviceAlarmCount(data?.totalItems || 0));
            dispatch(setIsLoadingStatus({ isLoadingAlarms: false }));
          }
        }
      }

    } catch (error: any) {
      dispatch(setIsLoadingStatus({ isLoadingDeviceDetail: false, isLoadingAlarms: false }));
      dispatch(setOpenDeviceDrawer(false));
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  const exportHandler = async () => {
    try {
      const response: any = await exportDevices(args);
      if (response?.error) throw response?.error;
      exportInCSV(response, "Devices");
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  return {
    permissions,
    data,
    isLoading,
    nodesColumns,
    refetch,
    handleRowClicked,
    selectedRowData,
    setSelectedRowData,
    rowClicked,
    setRowClicked,
    exportHandler,
    exportStatus,
    setSortOrderToNode,
  };
};
